import { ART_PAL, ball, body, fade, mix, n, shade, star, tint, vgrad, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Koi Pond scene behind the game zone: a night pond (dawn in light), the moon with its reflection top right, a string
 * of far lanterns top left, lily pads and lotuses in the corners, one koi shadow under the water. The centre stays calm
 * (the reels sit there): nothing but water and a soft pool of light. Motifs are marked `data-motif="<x>"` (tests).
 * No filters, no text: a static image, cheap to paint.
 */
export function koiPondScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `sk${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const horizon = wide ? 300 : 230;

  const sky = P.dark ? mix(P.bg, P.blue, 0.2) : mix(P.purple, '#FFFFFF', 0.84);
  const skyLow = P.dark ? mix(P.bg, P.teal, 0.1) : mix(P.pink, '#FFFFFF', 0.86);
  const water = P.dark ? mix(P.bg, P.teal, 0.16) : mix(P.teal, '#FFFFFF', 0.74);
  const deep = P.dark ? mix(P.bg, P.teal, 0.07) : mix(P.teal, '#FFFFFF', 0.64);
  const shore = P.dark ? mix(P.bg, P.teal, 0.05) : mix(P.teal, P.purple, 0.5);
  const moon = P.dark ? '#FFF1C2' : '#FFE7A3';
  const pad = P.dark ? shade(P.green, 0.45) : mix(P.green, '#FFFFFF', 0.35);
  const lotus = P.dark ? shade(P.pink, 0.15) : tint(P.pink, 0.2);
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.6);
  const ink = P.dark ? '#FFFFFF' : shade(P.teal, 0.4);
  const o = (dark: number, light: number) => n(P.dark ? dark : light);

  /** a lily pad with the notch towards the viewer */
  const lily = (cx: number, cy: number, rx: number, rot: number) => {
    const ry = rx * 0.34;
    const at = (a: number) => `${n(cx + rx * Math.cos(a))} ${n(cy + ry * Math.sin(a))}`;
    return (
      `<path data-motif="${Math.round(cx)}" d="M${cx} ${cy}L${at(1.3)}A${n(rx)} ${n(ry)} 0 1 0 ${at(1.75)}Z" ` +
      `transform="rotate(${rot} ${cx} ${cy})" fill="url(#${p}pd)"/>`
    );
  };
  const flower = (cx: number, cy: number, s: number) =>
    `<g data-motif="${Math.round(cx)}" transform="translate(${cx} ${cy}) scale(${s})" fill="url(#${p}lo)">` +
    [-60, -30, 30, 60, -10, 10]
      .map((a) => `<path d="M0 0C-7 -10 -6 -24 0 -32C6 -24 7 -10 0 0Z" transform="rotate(${a})"/>`)
      .join('') +
    `<circle cy="-5" r="4" fill="${gold}"/></g>`;
  /** a far lantern: glow + small red body + gold caps */
  const lantern = (x: number, y: number, r: number) =>
    `<g data-motif="${Math.round(x)}"><circle cx="${n(x)}" cy="${n(y)}" r="${n(r * 4)}" fill="url(#${p}lh)"/>` +
    `<ellipse cx="${n(x)}" cy="${n(y)}" rx="${n(r)}" ry="${n(r * 0.9)}" fill="url(#${p}ln)"/>` +
    `<rect x="${n(x - r * 0.45)}" y="${n(y - r * 1.15)}" width="${n(r * 0.9)}" height="${n(r * 0.3)}" fill="${gold}"/>` +
    `<rect x="${n(x - r * 0.45)}" y="${n(y + r * 0.85)}" width="${n(r * 0.9)}" height="${n(r * 0.3)}" fill="${gold}"/></g>`;
  /** a string of lanterns sagging between (x0, y0) and (x1, y0) */
  const garland = (x0: number, x1: number, y0: number, sag: number, count: number, r: number) => {
    const mx = (x0 + x1) / 2;
    let s = `<path d="M${x0} ${y0}Q${mx} ${y0 + sag * 2} ${x1} ${y0}" fill="none" stroke="${ink}" stroke-opacity="${o(0.18, 0.3)}" stroke-width="1.5"/>`;
    for (let i = 1; i <= count; i++) {
      const t = i / (count + 1);
      const x = x0 + (x1 - x0) * t;
      const y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * (y0 + sag * 2) + t * t * y0;
      s += `<path d="M${n(x)} ${n(y)}V${n(y + r * 0.9)}" stroke="${ink}" stroke-opacity="${o(0.2, 0.3)}"/>` + lantern(x, y + r * 2, r);
    }
    return s;
  };
  const ripples = (cx: number, cy: number, rx: number, k: number) => {
    let s = '';
    for (let i = 0; i < k; i++)
      s += `<ellipse cx="${cx}" cy="${cy}" rx="${n(rx * (1 + i * 0.55))}" ry="${n(rx * 0.22 * (1 + i * 0.55))}"/>`;
    return `<g fill="none" stroke="${ink}" stroke-opacity="${o(0.07, 0.22)}" stroke-width="1.5">${s}</g>`;
  };

  const defs =
    `<defs>${vgrad(`${p}sk`, sky, skyLow, skyLow)}${vgrad(`${p}wt`, water, mix(water, deep, 0.5), deep)}` +
    fade(`${p}mh`, moon, P.dark ? 0.32 : 0.7) +
    ball(`${p}mo`, moon, 0.5, 0.12) +
    fade(`${p}lh`, P.orange, P.dark ? 0.35 : 0.22) +
    ball(`${p}ln`, P.dark ? shade(P.red, 0.1) : tint(P.red, 0.15), 0.45, 0.35) +
    body(`${p}pd`, pad, 0.25, 0.3, 0.3, 1) +
    body(`${p}lo`, lotus, 0.45, 0.25, 0.4, 1) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.45 : 0.5) +
    `</defs>`;

  const moonX = wide ? 1250 : 318;
  const moonY = wide ? 150 : 92;
  const moonR = wide ? 34 : 22;
  const koiShadow = (x: number, y: number, s: number, rot: number) =>
    `<path data-motif="${x}" d="M-46 2C-46 -12 -32 -20 -12 -20C8 -20 22 -12 32 -4L60 -26C56 -12 54 -2 52 2C54 8 57 18 62 28L32 6C22 13 8 18 -12 18C-32 18 -46 14 -46 2Z" ` +
    `transform="translate(${x} ${y}) rotate(${rot}) scale(${s})" fill="${shade(deep, 0.5)}" fill-opacity="${o(0.2, 0.08)}"/>`;

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}sk)"/>` +
    // stars (night only), all at the edges
    (P.dark
      ? (wide
          ? [
              [120, 40, 2.4],
              [560, 34, 1.8],
              [1120, 60, 2],
              [1500, 220, 2.2],
              [260, 180, 1.6],
            ]
          : [
              [40, 170, 1.8],
              [220, 30, 1.6],
              [370, 190, 1.8],
            ]
        )
          .map(([x, y, r]) => `<polygon points="${star(x as number, y as number, r as number, (r as number) * 0.35, 4)}" fill="${moon}" fill-opacity=".55"/>`)
          .join('')
      : '') +
    `<circle cx="${moonX}" cy="${moonY}" r="${moonR * 4}" fill="url(#${p}mh)"/>` +
    `<circle data-motif="${moonX}" cx="${moonX}" cy="${moonY}" r="${moonR}" fill="url(#${p}mo)" fill-opacity="${o(0.85, 0.95)}"/>` +
    // far shore
    `<path d="M0 ${horizon - 18}C${W * 0.12} ${horizon - 44} ${W * 0.24} ${horizon - 40} ${W * 0.36} ${horizon - 16}S${W * 0.62} ${horizon - 30} ${W * 0.78} ${horizon - 20}S${W * 0.94} ${horizon - 40} ${W} ${horizon - 26}V${horizon + 4}H0Z" fill="${shore}" fill-opacity="${o(0.95, 0.3)}"/>` +
    `<rect y="${horizon}" width="${W}" height="${H - horizon}" fill="url(#${p}wt)"/>` +
    // moon path on the water
    `<path d="${[0, 1, 2, 3]
      .map((i) => `M${n(moonX - 40 + i * 10)} ${horizon + 22 + i * 18}h${n(80 - i * 20)}`)
      .join('')}" stroke="${moon}" stroke-opacity="${o(0.22, 0.6)}" stroke-width="3" stroke-linecap="round"/>` +
    // the calm centre: a soft pool that lowers contrast behind the reels
    `<ellipse cx="${W / 2}" cy="${H * 0.55}" rx="${n(W * 0.38)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>`;

  if (wide) {
    art +=
      garland(190, 520, 120, 18, 5, 8) +
      ripples(260, 600, 60, 3) +
      ripples(1340, 520, 50, 2) +
      koiShadow(1240, 660, 1, -14) +
      lily(90, 470, 46, -4) +
      lily(170, 820, 90, 4) +
      lily(360, 870, 70, -8) +
      lily(60, 700, 60, 10) +
      lily(1250, 860, 80, -6) +
      lily(1470, 780, 96, 6) +
      lily(1540, 470, 44, -8) +
      flower(200, 808, 1.3) +
      flower(1480, 764, 1.5) +
      flower(70, 690, 0.8);
  } else {
    art +=
      garland(-10, 190, 40, 14, 3, 7) +
      ripples(80, 520, 26, 2) +
      lily(40, 740, 60, 6) +
      lily(360, 770, 56, -6) +
      lily(380, 420, 30, -8) +
      flower(46, 728, 0.9) +
      flower(352, 756, 0.8);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    defs +
    art +
    `</svg>`
  );
}

/** Header accent of Koi Pond: a string of tiny lanterns (repeat-x tile, 64 × 12). */
export function koiPondAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.4);
  const red = P.dark ? P.red : tint(P.red, 0.05);
  const ink = P.dark ? tint(P.teal, 0.2) : P.teal;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 12" width="64" height="12">` +
    `<path d="M0 1.5Q32 7 64 1.5" fill="none" stroke="${ink}" stroke-opacity=".55"/>` +
    `<ellipse cx="32" cy="7.6" rx="3.4" ry="3" fill="${red}"/>` +
    `<rect x="30.4" y="4" width="3.2" height="1.2" fill="${gold}"/><rect x="30.4" y="10.2" width="3.2" height="1.2" fill="${gold}"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'koi-pond', scene: koiPondScene, accent: koiPondAccent });
